import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BRAND } from "@/data/products";
import { ArrowRight, Sparkles } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
      <motion.span
        className={`block will-change-transform ${className}`}
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const ghostY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 overflow-hidden marble-bg"
    >
      {/* subtle marble backdrop grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#D4A93A 1px, transparent 1px), linear-gradient(90deg, #D4A93A 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* giant ghost word drifting on scroll */}
      <motion.div
        aria-hidden
        style={{ y: ghostY }}
        className="absolute -bottom-8 left-0 right-0 text-center font-serif text-[20vw] leading-none text-ink/[0.045] select-none pointer-events-none"
      >
        VARDAAN
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pb-20 pt-10 md:pt-14 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-center">
        {/* Left content */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gold/40 bg-cream-200/40"
          >
            <Sparkles className="w-3.5 h-3.5 text-gold-dark" />
            <span className="text-[11px] tracking-[0.28em] uppercase text-ink-soft">
              Established {BRAND.established} · Thane, India
            </span>
          </motion.div>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] text-ink mt-8">
            <MaskedLine delay={0.25}>Crafting</MaskedLine>
            <MaskedLine delay={0.4} className="italic gold-text">
              Divine Beauty
            </MaskedLine>
            <MaskedLine delay={0.55}>in Marble</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="text-base sm:text-lg text-ink-soft mt-8 max-w-xl leading-relaxed"
          >
            From the sacred stone of Makrana — the same marble that shaped the
            Taj Mahal — Vardaan Arts hand-carves temples, statues, fountains
            and sculptures for homes, temples and estates across the world.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <a
              href="#collections"
              data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-ink text-cream-100 text-[12px] tracking-[0.28em] uppercase hover:bg-gold transition-colors duration-500"
            >
              Explore the Collection
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
            <a
              href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
                "Hello Vardaan Arts, I saw your website and would like to enquire about a custom marble piece."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-3 px-8 py-4 border border-gold text-ink text-[12px] tracking-[0.28em] uppercase hover:bg-gold/10 transition-colors"
            >
              Chat on WhatsApp
            </a>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 1.15, ease: EASE }}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
          >
            {[
              { k: "11+", v: "Years of Craft" },
              { k: "1000+", v: "Temple Fittings" },
              { k: "16", v: "Categories" },
              { k: "100+", v: "Countries Exported" },
            ].map((s, i) => (
              <div key={i} className="border-l border-gold/40 pl-4">
                <div className="font-serif text-3xl md:text-4xl text-ink">
                  {s.k}
                </div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-ink-soft mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: layered marble image with arch + parallax */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="relative"
        >
          <div className="relative">
            {/* Big gold arch frame */}
            <div className="absolute inset-0 -m-4 md:-m-6 border border-gold/40 pointer-events-none rounded-t-[50%_28%]" />
            <div className="absolute -inset-1 border border-gold/20 pointer-events-none rounded-t-[50%_28%]" />

            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
              className="relative overflow-hidden bg-cream-200 rounded-t-[50%_28%]"
            >
              <motion.img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_vardaan-divine/artifacts/5siajrr6_IMG_20251220_103703.jpg"
                alt="Vardaan Arts marble temple centrepiece"
                style={{ y: imgY, scale: 1.18 }}
                className="w-full h-[520px] md:h-[620px] object-cover will-change-transform"
                data-testid="hero-image"
              />
            </motion.div>

            {/* Corner label */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
              className="absolute -left-4 top-8 md:-left-6 md:top-12 bg-cream-100 border border-gold/40 px-5 py-3 shadow-sm"
            >
              <div className="text-[10px] tracking-[0.28em] uppercase text-gold-dark">
                Signature Piece
              </div>
              <div className="font-serif text-lg text-ink mt-1">
                Peacock Inlay Mandir
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.35, ease: EASE }}
              className="absolute -right-4 bottom-8 md:-right-6 md:bottom-12 bg-ink-deep text-cream-100 px-5 py-3"
            >
              <div className="text-[10px] tracking-[0.28em] uppercase text-gold-light">
                Since 2014
              </div>
              <div className="font-serif text-lg mt-1">Makrana Grade 1</div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
